import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wrench, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function JobTypeMaster() {
  const [types, setTypes] = useState(null);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/job-card-types"); setTypes(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) { toast.error("Enter a job type name"); return; }
    setBusy(true);
    try {
      await api.post("/job-card-types", { name: newName.trim() });
      setNewName("");
      toast.success("Job type added");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (t) => { setEditId(t.id); setEditName(t.name); };

  const saveEdit = async () => {
    if (!editName.trim()) { toast.error("Name cannot be blank"); return; }
    try {
      await api.patch(`/job-card-types/${editId}`, { name: editName.trim() });
      setEditId(null);
      toast.success("Job type updated");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (t) => {
    if (!confirm(`Delete job type "${t.name}"? Job cards already using it keep their saved value.`)) return;
    try {
      await api.delete(`/job-card-types/${t.id}`);
      toast.success("Job type deleted");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!types) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl" data-testid="jt-master-page">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Wrench className="w-3 h-3"/> Settings</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid="jt-master-title">Job Type Master</h1>
        <div className="text-xs text-muted-foreground mt-2">Manage the Job Type options available on the Job Card entry form (Owner / Manager only).</div>
      </div>

      <div className="flex gap-2 mb-4">
        <Input data-testid="jt-new-name" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="New job type name…" className="rounded-sm bg-secondary border-border h-9 flex-1"/>
        <Button data-testid="jt-add-btn" onClick={add} disabled={busy} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-3 h-3 mr-1"/> Add</Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="jt-master-card">
        <div className="bg-secondary px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex justify-between">
          <span>Job Type</span><span>Actions</span>
        </div>
        {types.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm" data-testid="jt-master-empty">No job types configured.</div>}
        {types.map(t => (
          <div key={t.id} className="flex items-center justify-between px-4 py-2.5 border-t border-border hover:bg-secondary/40" data-testid={`jt-row-${t.id}`}>
            {editId === t.id ? (
              <div className="flex items-center gap-2 flex-1 mr-3">
                <Input data-testid={`jt-edit-input-${t.id}`} value={editName} onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }}
                  className="rounded-sm bg-secondary border-border h-8 text-sm flex-1" autoFocus/>
                <Button data-testid={`jt-save-${t.id}`} size="sm" onClick={saveEdit} className="h-7 px-2 rounded-sm bg-primary text-primary-foreground"><Check className="w-3 h-3"/></Button>
                <Button data-testid={`jt-cancel-${t.id}`} size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-7 px-2"><X className="w-3 h-3"/></Button>
              </div>
            ) : (
              <span className="text-sm">{t.name}{t.is_default ? <span className="ml-2 text-[9px] uppercase tracking-wider text-muted-foreground">default</span> : null}</span>
            )}
            <div className="flex items-center gap-1">
              <Button data-testid={`jt-edit-${t.id}`} size="sm" variant="ghost" onClick={() => startEdit(t)} className="h-7 px-2"><Pencil className="w-3 h-3"/></Button>
              <Button data-testid={`jt-del-${t.id}`} size="sm" variant="ghost" onClick={() => del(t)} className="h-7 px-2 text-destructive"><Trash2 className="w-3 h-3"/></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground mt-3 uppercase tracking-wider">{types.length} job type{types.length === 1 ? "" : "s"} configured · changes apply to the Job Card form immediately</div>
    </div>
  );
}
